import React from 'react';
import Select, { components } from 'react-select';
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc';

const compareValues = (a, b, type) => {
  if (a === null || a === undefined) return 1;
  if (b === null || b === undefined) return -1;

  switch (type) {
    case 'alpha':
      return String(a).localeCompare(String(b));
    case 'number':
      return Number(a) - Number(b);
    case 'date':
      return new Date(a) - new Date(b);
    default:
      return 0;
  }
};

export const getDirectionOptions = type => {
  switch (type) {
    case 'alpha':
      return [
        { label: 'A to Z', value: 'asc' },
        { label: 'Z to A', value: 'desc' }
      ];
    case 'number':
      return [
        { label: 'Lowest to Highest', value: 'asc' },
        { label: 'Highest to Lowest', value: 'desc' }
      ];
    case 'date':
      return [
        { label: 'Oldest to Newest', value: 'asc' },
        { label: 'Newest to Oldest', value: 'desc' }
      ];
    default:
      return [
        { label: 'Ascending', value: 'asc' },
        { label: 'Descending', value: 'desc' }
      ];
  }
};

export const resolveSortOptions = (sortRules = [], sortFieldDefinitions = []) =>
  sortRules
    .map(rule => {
      const fieldDef = sortFieldDefinitions.find(field => field.value === rule.field);
      if (!fieldDef) return null;

      return {
        ...fieldDef,
        direction: rule.direction || 'asc'
      };
    })
    .filter(Boolean);

export const formatSortRulesSummary = (sortRules = [], sortFieldDefinitions = []) =>
  sortRules
    .map(rule => {
      const fieldDef = sortFieldDefinitions.find(field => field.value === rule.field);
      if (!fieldDef) return null;

      const directionLabel =
        getDirectionOptions(fieldDef.type).find(option => option.value === rule.direction)?.label || rule.direction;

      return `${fieldDef.label} (${directionLabel})`;
    })
    .filter(Boolean)
    .join(', then ');

const getNestedValue = (obj, value, key) => {
  if (obj[value] === undefined || obj[value] === null) return null;

  return key ? obj[value][key] : obj[value];
};

export const multiSortArray = (arr, sortOptions) => {
  if (!sortOptions?.length) return arr;

  return [...arr].sort((a, b) => {
    for (const option of sortOptions) {
      let aValue = option.isObject ? getNestedValue(a, option.value, option.key) : a[option.value];
      let bValue = option.isObject ? getNestedValue(b, option.value, option.key) : b[option.value];
      if (option.defaultValue !== undefined) {
        if (aValue === null || aValue === undefined) aValue = option.defaultValue;
        if (bValue === null || bValue === undefined) bValue = option.defaultValue;
      }
      const comparison = compareValues(aValue, bValue, option.type);
      if (comparison === 0) continue;

      if (option.direction === 'asc') return comparison;
      if (option.direction === 'desc') return -comparison;

      // Legacy behavior when direction is not specified.
      if (option.type !== 'alpha') return -comparison;
      return comparison;
    }

    return 0;
  });
};

function arrayMove(array, from, to) {
  const slicedArray = array.slice();
  slicedArray.splice(to < 0 ? array.length + to : to, 0, slicedArray.splice(from, 1)[0]);
  return slicedArray;
}

const SortableMultiValue = SortableElement(props => {
  const onMouseDown = e => {
    e.preventDefault();
    e.stopPropagation();
  };
  const innerProps = { ...props.innerProps, onMouseDown };
  return <components.MultiValue {...props} innerProps={innerProps} />;
});

const SortableMultiValueLabel = SortableHandle(props => {
  // Retrieve the index of the current item and add 1

  return <span>{props.children}</span>;
});
const SortableSelect = SortableContainer(Select);

const MultiSelectSort = ({ disabled, sortOptions = [], selectedSortOptions = [], onSelectedSortOptionsChange }) => {
  const onChange = selectedOptions => onSelectedSortOptionsChange(selectedOptions);

  const onSortEnd = ({ oldIndex, newIndex }) => {
    const newValue = arrayMove(selectedSortOptions, oldIndex, newIndex);
    onSelectedSortOptionsChange(newValue);
  };

  return (
    <SortableSelect
      useDragHandle
      isDisabled={disabled}
      axis="xy"
      onSortEnd={onSortEnd}
      distance={4}
      getHelperDimensions={({ node }) => node.getBoundingClientRect()}
      isMulti
      options={sortOptions}
      value={selectedSortOptions}
      onChange={onChange}
      components={{
        MultiValue: SortableMultiValue,
        MultiValueLabel: SortableMultiValueLabel
      }}
      closeMenuOnSelect={false}
    />
  );
};
export default MultiSelectSort;
